import React from "react";
import { useEffect, useRef } from "react";
import { Message } from "@twilio/conversations/lib/message";

import Container from "./Container";
import TextMessage from "./TextMessage";
import useVideoContext from "hooks/session/useVideoContext";
import MediaMessage from "./MediaMessage";
import { Avatar, Box, Grid, GridItem } from "@chakra-ui/react";

interface MessageListProps {
  messages: Message[];
  componentHeight: number;
}

const getFormattedTime = (message?: Message): string | undefined =>
  message?.dateCreated
    .toLocaleTimeString("en-us", { hour: "numeric", minute: "numeric" })
    .toLowerCase();

const MessageList: React.FC<MessageListProps> = ({
  messages, componentHeight
}: MessageListProps) => {
  const { room } = useVideoContext();
  const localParticipant = room!.localParticipant;
  const divRef = useRef<null | HTMLDivElement>(null);

  useEffect(() =>{
    divRef.current?.scrollIntoView({block: 'end', behavior: 'smooth' });
  },[]);


  return (
    <div style={{
      overflowY: "auto", scrollbarWidth: "thin"
    }}>
      <Box height={componentHeight}>
        <Container messages={messages} >
          {messages.map((message, idx) => {
            const time = getFormattedTime(message)!;
            const previousTime = getFormattedTime(messages[idx - 1]);

            // Display the MessageInfo component when the author or formatted timestamp differs from the previous message
            const shouldDisplayMessageInfo =
              time !== previousTime || message.author !== messages[idx - 1]?.author;

            const isLocalParticipant = localParticipant.identity === message.author;

            return (
              <Grid
                key={message.sid}
                alignItems="center"
                templateColumns="fit-content(100%) 1fr fit-content(100%)"
                gap={3}
              >
                <GridItem>
                  <Avatar name={message.author} hidden={isLocalParticipant} />
                </GridItem>
                <Grid justifyItems={isLocalParticipant ? "end" : "start"}>
                  {shouldDisplayMessageInfo && (
                    <Box fontSize="14px">
                      {`${message.author}${isLocalParticipant ? " (You)" : ""}`}
                    </Box>
                  )}
                  {message.type === "text" && (
                    <TextMessage
                      body={message.body}
                      isLocalParticipant={isLocalParticipant}
                    />
                  )}
                  {message.type === "media" && (
                    <MediaMessage media={message.media} />
                  )}
                  {shouldDisplayMessageInfo && <Box fontSize="10px">{time}</Box>}
                </Grid>
                <GridItem>
                  <Avatar name={message.author} hidden={!isLocalParticipant} />
                </GridItem>
              </Grid>
            );
          })}
        </Container>
        <div ref={divRef} />
      </Box>
    </div>
  );
};

export default MessageList;
